/* global google */
import React, { useEffect, useRef, useState } from "react";
import {
  withScriptjs,
  withGoogleMap,
  GoogleMap,
  Marker,
  InfoWindow
} from "react-google-maps";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  ListGroup,
  ListGroupItem
} from "shards-react";
import api from "../common/api";
import { get_request } from "../common/axios";

const DEFAULT_CENTER = { lat: -34.603722, lng: -58.381592 };
const ACTIVE_COLOR = "#28a745";
const INACTIVE_COLOR = "#dc3545";
const REFRESH_INTERVAL_MS = 30000;
const markerIcons = {};

const getPosition = vehicle => ({
  lat: parseFloat(vehicle.lat),
  lng: parseFloat(vehicle.long)
});

const isValidPosition = position =>
  !Number.isNaN(position.lat) && !Number.isNaN(position.lng);

const getMarkerIcon = vehicle => {
  const isActive = Number(vehicle.ignition) === 1;
  const color = isActive ? ACTIVE_COLOR : INACTIVE_COLOR;
  const cacheKey = isActive ? "active" : "inactive";

  if (markerIcons[cacheKey]) {
    return markerIcons[cacheKey];
  }

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="0 0 44 44">
      <path d="M10 26h24c1.7 0 3 1.3 3 3v5h-4.2c-.8 2.3-2.9 4-5.4 4s-4.6-1.7-5.4-4h-4c-.8 2.3-2.9 4-5.4 4s-4.6-1.7-5.4-4H7v-5c0-1.7 1.3-3 3-3z" fill="${color}" stroke="#ffffff" stroke-width="2"/>
      <path d="M13 25l3.6-8.2c.5-1.1 1.6-1.8 2.8-1.8h10.2c1.2 0 2.3.7 2.8 1.8L36 25H13z" fill="${color}" stroke="#ffffff" stroke-width="2"/>
      <path d="M18.7 18h4.8v6h-7.4l2.6-6zM25.5 18h4.1l2.6 6h-6.7v-6z" fill="#ffffff" opacity="0.85"/>
      <circle cx="13" cy="34" r="3.2" fill="#333333" stroke="#ffffff" stroke-width="1.4"/>
      <circle cx="28" cy="34" r="3.2" fill="#333333" stroke="#ffffff" stroke-width="1.4"/>
    </svg>
  `;

  markerIcons[cacheKey] = {
    url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
    scaledSize: new google.maps.Size(44, 44),
    anchor: new google.maps.Point(22, 36),
    labelOrigin: new google.maps.Point(22, 52)
  };

  return markerIcons[cacheKey];
};

const getCenter = vehicles => {
  if (!vehicles.length) {
    return DEFAULT_CENTER;
  }

  const totals = vehicles.reduce(
    (acc, vehicle) => {
      const position = getPosition(vehicle);
      return {
        lat: acc.lat + position.lat,
        lng: acc.lng + position.lng
      };
    },
    { lat: 0, lng: 0 }
  );

  return {
    lat: totals.lat / vehicles.length,
    lng: totals.lng / vehicles.length
  };
};

const TrackingGoogleMap = withScriptjs(
  withGoogleMap(props => (
    <GoogleMap
      defaultZoom={11}
      defaultCenter={props.center}
      options={{
        fullscreenControl: true,
        mapTypeControl: true,
        mapTypeId: google.maps.MapTypeId.TERRAIN,
        mapTypeControlOptions: {
          mapTypeIds: [
            google.maps.MapTypeId.ROADMAP,
            google.maps.MapTypeId.TERRAIN,
            google.maps.MapTypeId.SATELLITE,
            google.maps.MapTypeId.HYBRID
          ]
        },
        streetViewControl: false,
        styles: [
            {
              featureType: "poi",
              stylers: [{ visibility: "off" }]
            },
            {
                featureType: "poi.business",
                stylers: [{ visibility: "off" }]
            },
            {
                featureType: "transit",
                stylers: [{ visibility: "off" }]
            }            
          ]        
      }}
    >
      {props.vehicles.map(vehicle => {
        const position = getPosition(vehicle);
        const isSelected =
          props.selectedVehicle && props.selectedVehicle.plate === vehicle.plate;

        return (
          <Marker
            key={vehicle.plate}
            position={position}
            icon={getMarkerIcon(vehicle)}
            label={{
              text: vehicle.plate,
              color: "#222222",
              fontWeight: "bold"
            }}
            onClick={() => props.onSelectVehicle(vehicle)}
          >
            {isSelected && (
              <InfoWindow onCloseClick={() => props.onSelectVehicle(null)}>
                <div>
                  <strong>{vehicle.plate}</strong>
                </div>
              </InfoWindow>
            )}
          </Marker>
        );
      })}
    </GoogleMap>
  ))
);

const VehicleDetail = ({ vehicle }) => {
  if (!vehicle) {
    return (
      <Card small>
        <CardHeader className="border-bottom">
          <h6 className="m-0">Detalle de unidad</h6>
        </CardHeader>
        <CardBody>
          <p className="mb-0 text-muted">
            Hacé click en una patente para ver la información satelital.
          </p>
        </CardBody>
      </Card>
    );
  }

  const ignition = Number(vehicle.ignition);

  return (
    <Card small>
      <CardHeader className="border-bottom">
        <h6 className="m-0">{vehicle.plate}</h6>
      </CardHeader>
      <CardBody className="p-0">
        <ListGroup flush small>
          <ListGroupItem className="px-2 py-1">
            Estado:{" "}
            <span
              className={ignition === 1 ? "text-success" : "text-danger"}
            >
              {ignition === 1 ? "Encendido" : "Apagado"}
            </span>
          </ListGroupItem>
          <ListGroupItem className="px-2 py-1">Dirección: {vehicle.address}</ListGroupItem>
          <ListGroupItem className="px-2">Velocidad: {vehicle.speed} km/h</ListGroupItem>
          <ListGroupItem className="px-2 py-1">Odómetro: {vehicle.odometer} km</ListGroupItem>
          <ListGroupItem className="px-2 py-1">Batería: {vehicle.battery} V</ListGroupItem>
          <ListGroupItem className="px-2 py-1">Última actualización: {vehicle.last_time}</ListGroupItem>
        </ListGroup>
      </CardBody>
    </Card>
  );
};

const TrackingMap = () => {
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const refreshCountdownRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    let nextRefreshAt = Date.now() + REFRESH_INTERVAL_MS;

    const updateRefreshCountdown = () => {
      if (!refreshCountdownRef.current) {
        return;
      }

      const secondsToRefresh = Math.max(
        0,
        Math.ceil((nextRefreshAt - Date.now()) / 1000)
      );

      refreshCountdownRef.current.textContent = secondsToRefresh;
    };

    const getTracking = () => {
      nextRefreshAt = Date.now() + REFRESH_INTERVAL_MS;
      updateRefreshCountdown();

      get_request(api.vehicles().tracking()).then(res => {
        if (!mounted) {
          return;
        }

        if (res.status === 200) {
          const trackingData = Array.isArray(res.data) ? res.data : [];
          const validVehicles = trackingData
            .map(vehicle => ({ ...vehicle, position: getPosition(vehicle) }))
            .filter(vehicle => isValidPosition(vehicle.position));

          setVehicles(validVehicles);
          setSelectedVehicle(selected =>
            selected
              ? validVehicles.find(vehicle => vehicle.plate === selected.plate) ||
                null
              : null
          );
          setError("");
        } else {
          setError("No se pudo obtener la información de seguimiento.");
        }

        setLoading(false);
      });
    };

    getTracking();
    const refreshInterval = setInterval(getTracking, REFRESH_INTERVAL_MS);
    const countdownInterval = setInterval(updateRefreshCountdown, 1000);

    return () => {
      mounted = false;
      clearInterval(refreshInterval);
      clearInterval(countdownInterval);
    };
  }, []);

  const googleApiKey = process.env.REACT_APP_GOOGLE_KEY;
  const center = getCenter(vehicles);

  return (
    <Container fluid className="main-content-container px-4 py-4">
      <Row className="mb-3">
        <Col>
          <h5 className="mb-0">
            Seguimiento satelital de flota{" "}
          </h5>
          <p className="text-muted" style={{ fontSize: "0.7rem" }}>
              Próximo refresco en{" "}
              <span ref={refreshCountdownRef}>
                {REFRESH_INTERVAL_MS / 1000}
              </span>{" "}
              segundos
            </p>
        </Col>
      </Row>
      {error && (
        <Row className="mb-3">
          <Col>
            <Card small>
              <CardBody>
                <p className="mb-0 text-danger">{error}</p>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
      <Row>
        <Col lg="9" md="12" className="mb-4">
          <Card small>
            <CardBody className="p-0">
              {loading ? (
                <div className="p-4 text-center">Cargando mapa...</div>
              ) : (
                <TrackingGoogleMap
                  googleMapURL={
                    "https://maps.googleapis.com/maps/api/js?key=" +
                    googleApiKey
                  }
                  loadingElement={<div style={{ height: `100%` }} />}
                  containerElement={<div style={{ height: `70vh` }} />}
                  mapElement={<div style={{ height: `100%` }} />}
                  vehicles={vehicles}
                  selectedVehicle={selectedVehicle}
                  center={center}
                  onSelectVehicle={setSelectedVehicle}
                />
              )}
            </CardBody>
          </Card>
        </Col>
        <Col lg="3" md="12">
          <VehicleDetail vehicle={selectedVehicle} />
          <Card small className="mt-3">
            <CardHeader className="border-bottom">
              <h6 className="m-0">Patentes</h6>
            </CardHeader>
            <CardBody className="p-0">
              <ListGroup flush small style={{ maxHeight: "40vh", overflowY: "auto" }}>
                {vehicles.slice().sort((a, b) => b.ignition - a.ignition).map(vehicle => {
                  const ignition = Number(vehicle.ignition);
                  const isSelected =
                    selectedVehicle && selectedVehicle.plate === vehicle.plate;

                  return (
                    <ListGroupItem
                      key={`list-${vehicle.plate}`}
                      className={isSelected ? "bg-light" : ""}
                      style={{ cursor: "pointer" }}
                      onClick={() => setSelectedVehicle(vehicle)}
                    >
                      <span
                        style={{
                          backgroundColor:
                            ignition === 1 ? ACTIVE_COLOR : INACTIVE_COLOR,
                          borderRadius: "50%",
                          display: "inline-block",
                          height: 10,
                          marginRight: 8,
                          width: 10
                        }}
                      />
                      {vehicle.plate}
                    </ListGroupItem>
                  );
                })}
              </ListGroup>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default TrackingMap;